import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Post = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentUser = auth.currentUser;

  useEffect(() => {
    const fetchPostData = async () => {
      try {
        const postDoc = await getDoc(doc(db, 'posts', id));
        if (postDoc.exists()) {
          const postData = postDoc.data();
          // Ensure likedBy is initialized as an array for safe checking
          if (!postData.likedBy) {
            postData.likedBy = [];
          }
          setPost(postData);

          const userDoc = await getDoc(doc(db, 'users', postData.userId));
          if (userDoc.exists()) {
            setUser(userDoc.data());
          }
        } else {
          console.log('Post not found');
        }
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPostData();
  }, [id]);

  const handleLike = async () => {
    if (!currentUser || !post) return;

    const postRef = doc(db, 'posts', id);
    const isLiked = post.likedBy?.includes(currentUser.uid);

    try {
      await updateDoc(postRef, {
        // Increment/Decrement the numerical 'likes' field
        likes: isLiked ? post.likes - 1 : post.likes + 1,
        // Update the array of UIDs who liked the post
        likedBy: isLiked
          ? arrayRemove(currentUser.uid)
          : arrayUnion(currentUser.uid),
      });
      
      // Optimistically update the local state
      setPost(prev => ({
        ...prev,
        likes: isLiked ? prev.likes - 1 : prev.likes + 1,
        likedBy: isLiked
          ? prev.likedBy.filter(uid => uid !== currentUser.uid)
          : [...(prev.likedBy || []), currentUser.uid],
      }));
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={styles.loader}>
        <CircularProgress size={40} sx={{ color: '#C00068' }} />
      </Box>
    );
  }

  if (!post || !user) {
    return (
      <Box sx={styles.container}>
        <Typography>Post not found.</Typography>
        <Button onClick={() => navigate('/profile')} variant="contained" sx={styles.backButton}>
          Back to Profile
        </Button>
      </Box>
    );
  }

  const isCurrentUserLiked = post.likedBy?.includes(currentUser?.uid);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton onClick={() => navigate('/profile')} sx={styles.backButton}>
          <ArrowBackIcon sx={{ color: '#C00068' }} />
        </IconButton>
        <Typography variant="h6" sx={styles.username}>
          {user.firstName || 'Unknown User'}
        </Typography>
      </Box>
      
      <Box sx={styles.postContent}>
        <Avatar 
          src={user.profilePhoto || 'https://via.placeholder.com/40'} 
          sx={styles.avatar} 
          onClick={() => navigate('/profile')} // Navigate to user's profile
        />
        <Box sx={styles.postDetails}>
          <Typography variant="body1" sx={styles.caption}>
            {post.caption}
          </Typography>
          <Box
            component="img"
            src={post.imageUrl || 'https://via.placeholder.com/300?text=No+Image'}
            alt="Post content"
            sx={styles.postImage}
          />
        </Box>
      </Box>

      <Box sx={styles.actions}>
        <IconButton onClick={handleLike} sx={styles.likeButton} disabled={!currentUser}>
          <FavoriteIcon sx={{ color: isCurrentUserLiked ? '#C00068' : '#888' }} />
        </IconButton>
        <Typography sx={styles.likesCount}>
          {post.likes || 0} Likes
        </Typography>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FFF',
    pt: 2,
    pb: '60px',
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    p: '10px 16px',
    borderBottom: '1px solid #eee',
  },
  backButton: {
    mr: 2,
  },
  username: {
    fontWeight: 'bold',
    color: '#333',
  },
  postContent: {
    p: '16px',
    display: 'flex',
    alignItems: 'flex-start',
  },
  avatar: {
    width: 40,
    height: 40,
    mr: 2,
    border: '2px solid #C00068',
    cursor: 'pointer',
  },
  postDetails: {
    flexGrow: 1,
  },
  caption: {
    mb: 2,
    color: '#333',
  },
  postImage: {
    width: '100%',
    maxHeight: 700,
    objectFit: 'cover',
    borderRadius: 8,
  },
  actions: {
    p: '16px',
    display: 'flex',
    alignItems: 'center',
    borderTop: '1px solid #eee',
  },
  likeButton: {
    mr: 2,
  },
  likesCount: {
    color: '#666',
  },
};

export default Post;